import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, Loader2 } from "lucide-react";

export default function SuperAdminWalletDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [adjustAmt, setAdjustAmt] = useState("");
  const [reason, setReason] = useState("manual_adjustment");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/superadmin/wallets/${id}`);
      setData(data);
    } catch { toast.error("Couldn't load tenant"); }
  };
  useEffect(() => { refresh(); }, [id]);

  const adjust = async () => {
    const amount = parseFloat(adjustAmt);
    if (!amount) return toast.error("Enter a non-zero amount");
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/superadmin/wallets/${id}/adjust`, { amount, reason });
      toast.success(`Wallet ${amount > 0 ? "credited" : "debited"} · new balance ₹${res.balance.toFixed(2)}`);
      setAdjustAmt("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjust failed"); }
    finally { setSubmitting(false); }
  };

  if (!data) return <div className="p-10 text-center"><Loader2 size={18} className="animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="superadmin-wallet-detail">
      <div>
        <Link to="/superadmin/wallets" className="text-xs text-[hsl(var(--kb-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--kb-primary))]">
          <ArrowLeft size={12} /> Back to wallets
        </Link>
        <h1 className="font-display text-3xl mt-1">{data.tenant.brand_name || data.tenant.name || data.tenant.slug}</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))]">/t/{data.tenant.slug} · {data.tenant.status}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Stat label="Balance" value={`₹${data.balance.toFixed(2)}`} />
        <Stat label="MTD spend" value={`₹${data.mtd_spend.toFixed(2)}`} />
      </div>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-5" data-testid="adjust-wallet">
        <h2 className="font-display text-lg mb-2">Adjust balance</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))] mb-3">
          Positive number to credit, negative to debit. Every change is logged.
        </p>
        <div className="grid sm:grid-cols-[160px_1fr_120px] gap-2">
          <input className="kb-input" type="number" placeholder="±amount (₹)" value={adjustAmt} onChange={(e) => setAdjustAmt(e.target.value)} data-testid="adjust-amt" />
          <input className="kb-input" placeholder="Reason / note" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" />
          <button type="button" onClick={adjust} disabled={submitting} className="kb-btn-primary" data-testid="adjust-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
          </button>
        </div>
      </section>

      <section className="bg-white border border-[hsl(var(--kb-border))]">
        <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] font-medium">MTD spend by service</div>
        {!data.by_service?.length ? (
          <div className="p-6 text-sm text-[hsl(var(--kb-muted))]">No usage this month.</div>
        ) : (
          <table className="w-full text-sm" data-testid="by-service-table">
            <thead className="bg-[hsl(var(--kb-surface-alt))]">
              <tr><th className="p-3 text-left">Service</th><th className="p-3 text-right">Units</th><th className="p-3 text-right">Events</th><th className="p-3 text-right">Spend</th></tr>
            </thead>
            <tbody>
              {data.by_service.map((s) => (
                <tr key={s.service_key} className="border-t border-[hsl(var(--kb-border))]">
                  <td className="p-3 font-mono text-xs">{s.service_key}</td>
                  <td className="p-3 text-right">{s.total_units}</td>
                  <td className="p-3 text-right">{s.events}</td>
                  <td className="p-3 text-right font-medium">₹{s.total_amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white border border-[hsl(var(--kb-border))]">
        <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] font-medium">Recent transactions</div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--kb-surface-alt))]">
            <tr>
              <th className="p-3 text-left">When</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Detail</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3 text-right">Balance after</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent_transactions || []).map((t) => (
              <tr key={t.id} className="border-t border-[hsl(var(--kb-border))]">
                <td className="p-3">{new Date(t.created_at).toLocaleString()}</td>
                <td className="p-3">{t.type === "credit"
                  ? <span className="text-green-700 inline-flex items-center gap-1"><ArrowUp size={10} /> Credit</span>
                  : <span className="text-red-700 inline-flex items-center gap-1"><ArrowDown size={10} /> Debit</span>}</td>
                <td className="p-3 font-mono">{t.service_key || t.reason || "—"}</td>
                <td className="p-3 text-right">{t.type === "credit" ? "+" : "−"}₹{t.amount.toFixed(2)}</td>
                <td className="p-3 text-right">₹{t.balance_after.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
      <div className="text-xs text-[hsl(var(--kb-muted))] uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-display">{value}</div>
    </div>
  );
}
